import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type WriterDocState =
  | 'clean'
  | 'dirty'
  | 'materializing'
  | 'compiling'
  | 'success'
  | 'failed';

/**
 * Live subscription to the document state machine for a Writer project.
 * The state advances through the drain worker and writer-compile worker,
 * with Realtime pushes from `writer_projects`. Falls back to a periodic
 * refetch when Realtime is unavailable.
 */
export function useWriterDocState(projectId: string | undefined): {
  docState: WriterDocState | null;
  updatedAt: string | null;
} {
  const [docState, setDocState] = useState<WriterDocState | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setDocState(null);
      setUpdatedAt(null);
      return;
    }
    let cancelled = false;

    const refresh = async () => {
      const { data } = await supabase
        .from('writer_projects')
        .select('doc_state, doc_state_updated_at')
        .eq('id', projectId)
        .maybeSingle<{ doc_state: WriterDocState; doc_state_updated_at: string }>();
      if (cancelled || !data) return;
      setDocState(data.doc_state);
      setUpdatedAt(data.doc_state_updated_at);
    };

    refresh();
    const poll = setInterval(refresh, 5000);

    const channel = supabase
      .channel(`writer_projects:doc_state:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'writer_projects',
          filter: `id=eq.${projectId}`,
        },
        (payload) => {
          const next = payload.new as {
            doc_state?: WriterDocState;
            doc_state_updated_at?: string;
          };
          if (next.doc_state) setDocState(next.doc_state);
          if (next.doc_state_updated_at) setUpdatedAt(next.doc_state_updated_at);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  return { docState, updatedAt };
}
